import type { QueryClient } from '@tanstack/react-query';
import { Card } from 'flowbite-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ContentLoader } from '@/components/loaders/content-loader';
import {
  getCourtsQueryOptions,
  useCourts,
} from '@/features/courts/api/get-courts';
import { DodgeballPreview } from '@/features/courts/components/sports/dodgeball/dodgeball-preview';
import { useTournament } from '@/features/tournaments/api/get-tournament';
import { useActionCable } from '@/lib/action-cable';
import type { Court } from '@/types/api';

const SPORT_PREVIEWS = {
  dodgeball: DodgeballPreview,
};

export const courtsLoader = (queryClient: QueryClient) => async () => {
  const query = getCourtsQueryOptions();

  return (
    queryClient.getQueryData(query.queryKey) ??
    (await queryClient.fetchQuery(query))
  );
};

export const CourtsPage = () => {
  const cable = useActionCable();
  const courtsQuery = useCourts();
  const tournamentQuery = useTournament();
  const [overwrittenCourtData, setOverwrittenCourtData] = useState<{
    [key: string]: Court;
  }>({});

  useEffect(() => {
    if (tournamentQuery.data) {
      const { tournament } = tournamentQuery.data;

      const channel = cable.subscriptions.create(
        {
          channel: 'Scoreboard::CourtsChannel',
          tournament_id: tournament.id,
        },
        {
          received: (data: any) => {
            setOverwrittenCourtData((prev) => ({
              ...prev,
              [data.id]: data,
            }));
          },
        },
      );

      return () => {
        channel.unsubscribe();
      };
    }
  }, [cable, tournamentQuery.data]);

  if (courtsQuery.isLoading) {
    return <ContentLoader />;
  }

  if (!courtsQuery.data) {
    return null;
  }

  const { courts: originalCourtData } = courtsQuery.data;

  const courts = originalCourtData.map(
    (court) => overwrittenCourtData[court.id] ?? court,
  );

  const courtCards = courts.map((court: Court) => {
    const SportSpecificPreview = court.active_match
      ? SPORT_PREVIEWS[court.active_match.scoreboard]
      : null;

    return (
      <Card
        key={court.id}
        className="min-h-52"
        theme={{
          root: {
            children: 'h-full',
          },
        }}
      >
        <Link
          to={`/courts/${court.id}`}
          className="flex h-full flex-col justify-between p-6"
        >
          <h3 className="mb-2 flex items-center justify-between text-center text-lg font-medium dark:text-white">
            <div>{court.name}</div>
            <div className="font-normal text-gray-500 dark:text-gray-100">
              {court.active_match?.label}
            </div>
          </h3>

          {court.active_match ? (
            SportSpecificPreview && (
              <SportSpecificPreview match={court.active_match} />
            )
          ) : (
            <div className="mt-4 flex flex-1 flex-col items-center justify-center text-gray-700 dark:text-gray-50">
              No active match
            </div>
          )}
        </Link>
      </Card>
    );
  });

  return (
    <section className="m-4 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {courtCards}
    </section>
  );
};
